import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { subscribeApiProductToApplicationQuery } from "../Apis/apiProducts";
import useToken from "./useToken";
import { mapProductData } from "../constants/map/product";
import { useDispatch, useSelector } from "react-redux";
import { fetchApplication } from "../actions";
import APIProductService from "../services/APIProductService";

const useApiProducts = (applicationId = null) => {
  const csrfToken = useToken();
  const userId = useSelector(({ applicationUser }) => applicationUser?.uid);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [apiProducts, setApiProducts] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setIsLoading(true);
        // Fetch products from the service
        const rawProducts = await APIProductService.getProductList(
          userId,
          csrfToken
        );

        // Get all products from the service (raw data)
        const allProducts = APIProductService.getAllProducts();

        // Map and parse the data for UI
        const mappedData = mapProductData(allProducts);

        const parsedData = Object.keys(mappedData)
          .map((id) => mappedData[id])
          .filter(
            (product) => !product.deprecated && !product.commercialProduct
          );

        setApiProducts(parsedData);
      } catch (error) {
        console.error("Error loading products for subscription:", error);
        setApiProducts([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();
  }, [userId, csrfToken]);

  // Helper function to extract error message from various formats
  const extractErrorMessage = (error) => {
    if (typeof error === "string") {
      return error;
    }
    if (error && typeof error === "object") {
      // Handle {code, description} format
      if (error.description) {
        return error.description;
      }
      // Handle {message} format
      if (error.message) {
        return error.message;
      }
      // Handle Error object
      if (error instanceof Error) {
        return error.message;
      }
      // Try to stringify if it's a simple object
      if (error.code) {
        return `Error ${error.code}: ${
          error.description || "An error occurred"
        }`;
      }
    }
    return "Something went wrong. Please try again";
  };

  const clearError = () => {
    setErrorMessage(null);
  };

  const subscribeApiProduct = useMutation(
    (data) => subscribeApiProductToApplicationQuery(csrfToken, data, userId),
    {
      onMutate: () => {
        // Clear any existing error when starting a new mutation
        clearError();
      },
      onSuccess: () => {
        // TODO: THIS MUST BE REPLACED WITH ANOTHER MUTATION
        dispatch(fetchApplication({ applicationId }));
        clearError();
      },
      onError: (error) => {
        // React Query wraps the error, so we need to check for the message property first
        let message;
        if (error instanceof Error) {
          message = error.message;
        } else {
          message = extractErrorMessage(error);
        }
        setErrorMessage(message);
      },
    }
  );

  return {
    apiProducts,
    isLoading,
    subscribeApiProduct,
    errorMessage,
    clearError,
  };
};

export default useApiProducts;
